import { useState, useEffect } from "react";
import { Upload, RefreshCw, CheckCircle2, AlertTriangle, Link2, PoundSterling, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatCurrency } from "@/lib/calculations";
import { useIsMobile } from "@/components/custom/AppShell";

export default function PaymentMatching() {
  const isMobile = useIsMobile();
  const [tab, setTab] = useState("invoices");

  // Invoice matching state
  const [bankStatus, setBankStatus] = useState({ connected: false });
  const [matches, setMatches] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  // Expenses state
  const [expBankStatus, setExpBankStatus] = useState({ connected: false });
  const [expTxns, setExpTxns] = useState([]);
  const [expLoading, setExpLoading] = useState(false);
  const [expImporting, setExpImporting] = useState(false);

  useEffect(() => {
    api.get("/bank/status").then(r => setBankStatus(r.data)).catch(() => {});
    api.get("/bank/overdue-invoices").then(r => setOverdue(r.data)).catch(() => {});
    api.get("/bank/expenses-status").then(r => setExpBankStatus(r.data)).catch(() => {});
  }, []);

  // Invoice functions
  const fetchMatches = async () => {
    setLoading(true);
    try {
      const resp = await api.post("/bank/match-payments");
      setMatches(resp.data);
      if (resp.data.length === 0) toast.info("No new payment matches found");
    } catch (err) { console.error(err); toast.error("Failed to fetch"); }
    finally { setLoading(false); }
  };

  const handleCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await api.post("/bank/import-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setMatches(resp.data.results || []);
      toast.success(`${resp.data.imported} transactions, ${resp.data.matched} matches`);
    } catch (err) { console.error(err); toast.error("Import failed"); }
    finally { setImporting(false); e.target.value = ""; }
  };

  const confirmMatch = async (invoiceId, amount, date) => {
    try {
      await api.post("/bank/confirm-payment", { invoice_id: invoiceId, amount, date });
      toast.success("Payment confirmed");
      setMatches(prev => prev.map(m => ({ ...m, matches: m.matches.filter(match => match.invoice_id !== invoiceId) })).filter(m => m.matches.length > 0));
      api.get("/bank/overdue-invoices").then(r => setOverdue(r.data)).catch(() => {});
    } catch (err) { console.error(err); toast.error("Failed"); }
  };

  // Expense functions
  const fetchExpenses = async () => {
    setExpLoading(true);
    try {
      const resp = await api.post("/bank/match-expenses");
      setExpTxns(resp.data);
      if (resp.data.length === 0) toast.info("No new expense transactions found");
    } catch (err) { console.error(err); toast.error("Failed"); }
    finally { setExpLoading(false); }
  };

  const handleExpCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setExpImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const resp = await api.post("/bank/import-expenses-csv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExpTxns(resp.data.transactions || []);
      toast.success(`${resp.data.imported} expense transactions imported`);
    } catch (err) { console.error(err); toast.error("Import failed"); }
    finally { setExpImporting(false); e.target.value = ""; }
  };

  const confirmExpense = async (txn, category, merchant) => {
    try {
      await api.post("/expenses", {
        merchant: merchant || txn.suggested_merchant || "Unknown",
        description: txn.description,
        amount: txn.amount,
        vat_amount: Math.round((txn.amount / 6) * 100) / 100,
        date: txn.date || new Date().toISOString().split("T")[0],
        category: category || txn.suggested_category || "other",
        confirmed: true,
      });
      toast.success("Expense confirmed");
      setExpTxns(prev => prev.filter(t => t !== txn));
    } catch (err) { console.error(err); toast.error("Failed"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <div className={isMobile ? "px-4 pt-4 pb-3" : "px-8 pt-8 pb-4"}>
        <h1 className={isMobile ? "text-lg font-semibold mb-4" : "ui-page-title mb-4"}>Payment Matching</h1>

        {/* Tabs */}
        <div className="flex gap-1 mb-6">
          <button onClick={() => setTab("invoices")} className="px-4 py-2 rounded-lg text-sm font-medium transition-colors" style={{ background: tab === "invoices" ? "var(--primary)" : "var(--surface)", color: tab === "invoices" ? "#fff" : "var(--text-secondary)", border: `1px solid ${tab === "invoices" ? "var(--primary)" : "var(--border)"}` }}>
            <PoundSterling className="w-3.5 h-3.5 inline mr-1" /> Invoices
          </button>
          <button onClick={() => setTab("expenses")} className="px-4 py-2 rounded-lg text-sm font-medium transition-colors" style={{ background: tab === "expenses" ? "var(--primary)" : "var(--surface)", color: tab === "expenses" ? "#fff" : "var(--text-secondary)", border: `1px solid ${tab === "expenses" ? "var(--primary)" : "var(--border)"}` }}>
            <Receipt className="w-3.5 h-3.5 inline mr-1" /> Expenses
          </button>
        </div>
      </div>

      <div className={isMobile ? "px-4 pb-4" : "px-8 pb-8"}>
        {tab === "invoices" ? (
          <>
            {/* Invoice bank actions */}
            <div className="flex items-center gap-2 mb-4">
              <label className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer ${importing ? "opacity-50" : ""}`} style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
                <Upload className="w-3.5 h-3.5" /> {importing ? "Importing..." : "Import Bank CSV"}
                <input type="file" accept=".csv" className="hidden" onChange={handleCSVImport} disabled={importing} />
              </label>
              {bankStatus.connected && (
                <Button onClick={fetchMatches} disabled={loading} className="gap-1.5 h-9 px-4 text-sm" style={{ background: "var(--primary)", color: "#fff" }}>
                  <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> {loading ? "Fetching..." : "Fetch from Bank"}
                </Button>
              )}
            </div>

            {/* Bank status */}
            <div className="ui-surface p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: bankStatus.connected ? "#D1FAE5" : "#FEE2E2" }}>
                  <Link2 className="w-4 h-4" style={{ color: bankStatus.connected ? "#065F46" : "#991B1B" }} />
                </div>
                <div>
                  <p className="text-sm font-semibold">{bankStatus.connected ? "Virgin Money Connected" : "Income Bank Not Connected"}</p>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>{bankStatus.connected ? "Auto-matching payments" : "Connect via Settings or import CSV"}</p>
                </div>
              </div>
              {!bankStatus.connected && <a href="/settings" className="text-xs font-semibold" style={{ color: "var(--primary)" }}>Connect</a>}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              <div className="ui-surface p-4" style={{ minHeight: "80px" }}><p className="ui-label text-[10px]">Pending Matches</p><p className="text-2xl font-mono font-bold mt-1">{matches.length}</p></div>
              <div className="ui-surface p-4" style={{ minHeight: "80px" }}><p className="ui-label text-[10px]">Overdue</p><p className="text-2xl font-mono font-bold mt-1" style={{ color: overdue.length > 0 ? "var(--danger)" : "var(--text-primary)" }}>{overdue.length}</p></div>
              <div className="ui-surface p-4" style={{ minHeight: "80px" }}><p className="ui-label text-[10px]">Overdue Amount</p><p className="text-2xl font-mono font-bold mt-1" style={{ color: "var(--danger)" }}>{formatCurrency(overdue.reduce((s, o) => s + (o.total_inc_vat || 0), 0))}</p></div>
            </div>

            {/* Matches */}
            {matches.length > 0 && (
              <div className="mb-6">
                <h2 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: "var(--text-secondary)" }}>Confirm or Reject Matches</h2>
                <div className="space-y-3">
                  {matches.map((m, mi) => (
                    <div key={m.transaction?.id || `m-${mi}`} className="ui-surface overflow-hidden">
                      <div className="px-5 py-3 flex items-center gap-4" style={{ background: "var(--bg)", borderBottom: "1px solid var(--border)" }}>
                        <PoundSterling className="w-4 h-4" style={{ color: "var(--success)" }} />
                        <div className="flex-1">
                          <p className="text-sm font-mono font-bold" style={{ color: "var(--success)" }}>{formatCurrency(m.transaction.amount)}</p>
                          <p className="text-xs" style={{ color: "var(--text-muted)" }}>{m.transaction.description} · {m.transaction.date}</p>
                        </div>
                      </div>
                      {m.matches.map((match, mi2) => (
                        <div key={match.invoice_id || `match-${mi2}`} className="px-5 py-3 flex items-center gap-4" style={{ borderBottom: mi2 < m.matches.length - 1 ? "1px solid var(--border)" : "none" }}>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-mono font-bold" style={{ color: "var(--primary)" }}>{match.invoice_number || match.quote_number}</span>
                              <span className="text-xs" style={{ color: "var(--text-muted)" }}>{match.client_name} · {match.client_type}</span>
                              {match.commission_type !== "none" && <span className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: "#FEF3C7", color: "#92400E" }}>{match.commission_type === "20+vat" ? "20%+VAT" : "12.5%+VAT"}</span>}
                            </div>
                            <p className="text-xs mt-0.5 truncate" style={{ color: "var(--text-muted)" }}>{match.site_address}</p>
                            <div className="flex items-center gap-3 mt-1 text-xs">
                              <span>Invoice: <strong>{formatCurrency(match.invoice_amount)}</strong></span>
                              <span>Expected: <strong>{formatCurrency(match.expected_payment)}</strong></span>
                              <span>Got: <strong style={{ color: "var(--success)" }}>{formatCurrency(match.transaction_amount)}</strong></span>
                              <span className="font-mono" style={{ color: match.score >= 70 ? "#15803D" : "#D97706" }}>{match.score}%</span>
                            </div>
                          </div>
                          <div className="flex gap-2 flex-shrink-0">
                            <Button size="sm" onClick={() => confirmMatch(match.invoice_id, m.transaction.amount, m.transaction.date)} className="gap-1" style={{ background: "var(--success)", color: "#fff" }}><CheckCircle2 className="w-3.5 h-3.5" /> Confirm</Button>
                            <Button size="sm" variant="outline" onClick={() => setMatches(prev => prev.map((pm, pi) => pi === mi ? { ...pm, matches: pm.matches.filter((_, i) => i !== mi2) } : pm).filter(pm => pm.matches.length > 0))}>Reject</Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Overdue */}
            {overdue.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: "var(--danger)" }}><AlertTriangle className="w-4 h-4 inline mr-1" /> Overdue — Payment Missing</h2>
                <div className="ui-surface overflow-hidden">
                  <table className="ui-table">
                    <thead><tr><th>Invoice</th><th>Client</th><th>Address</th><th className="text-right">Amount</th><th className="text-center">Days Overdue</th></tr></thead>
                    <tbody>
                      {overdue.map(o => (
                        <tr key={o.id}>
                          <td className="font-mono font-bold" style={{ color: "var(--primary)" }}>{o.invoice_number || o.quote_number}</td>
                          <td>{o.client_name} <span className="text-xs" style={{ color: "var(--text-muted)" }}>({o.client_type})</span></td>
                          <td className="text-xs truncate max-w-[200px]">{o.site_address}</td>
                          <td className="text-right font-mono font-bold">{formatCurrency(o.total_inc_vat)}</td>
                          <td className="text-center"><span className="font-bold" style={{ color: "var(--danger)" }}>{o.days_overdue}d</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {matches.length === 0 && overdue.length === 0 && (
              <div className="flex flex-col items-center py-16">
                <Link2 className="w-10 h-10 mb-4" style={{ color: "#9CA3AF" }} />
                <p style={{ fontSize: "15px", fontWeight: 600, color: "#374151" }}>No pending matches</p>
                <p style={{ fontSize: "13px", color: "#6B7280", marginTop: "8px", textAlign: "center", maxWidth: "360px" }}>Connect your bank account or import a CSV to start matching payments automatically</p>
                <div className="flex gap-3 mt-5">
                  <label className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer" style={{ border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
                    <Upload className="w-4 h-4" /> Import CSV
                    <input type="file" accept=".csv" className="hidden" onChange={handleCSVImport} />
                  </label>
                  <a href="/settings" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold" style={{ background: "#E05C2A", color: "#fff" }}>Connect Bank</a>
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Expenses tab */}
            <div className="flex items-center gap-2 mb-4">
              <label className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium cursor-pointer ${expImporting ? "opacity-50" : ""}`} style={{ background: "var(--bg)", border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
                <Upload className="w-3.5 h-3.5" /> {expImporting ? "Importing..." : "Import Expenses CSV"}
                <input type="file" accept=".csv" className="hidden" onChange={handleExpCSVImport} disabled={expImporting} />
              </label>
              {expBankStatus.connected && (
                <Button onClick={fetchExpenses} disabled={expLoading} className="gap-1.5 h-9 px-4 text-sm" style={{ background: "var(--primary)", color: "#fff" }}>
                  <RefreshCw className={`w-3.5 h-3.5 ${expLoading ? "animate-spin" : ""}`} /> {expLoading ? "Fetching..." : "Fetch from Bank"}
                </Button>
              )}
            </div>

            {/* Expenses bank status */}
            <div className="ui-surface p-4 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: expBankStatus.connected ? "#D1FAE5" : "#FEE2E2" }}>
                  <Receipt className="w-4 h-4" style={{ color: expBankStatus.connected ? "#065F46" : "#991B1B" }} />
                </div>
                <div>
                  <p className="text-sm font-semibold">{expBankStatus.connected ? "Capital on Tap Connected" : "Expenses Bank Not Connected"}</p>
                  <p className="text-xs" style={{ color: "var(--text-muted)" }}>{expBankStatus.connected ? "Auto-fetching expense transactions" : "Connect via Settings or import CSV"}</p>
                </div>
              </div>
              {!expBankStatus.connected && <a href="/settings" className="text-xs font-semibold" style={{ color: "var(--primary)" }}>Connect</a>}
            </div>

            {/* Expense transactions to confirm */}
            {expTxns.length > 0 ? (
              <div>
                <h2 className="text-sm font-semibold uppercase tracking-wide mb-3" style={{ color: "var(--text-secondary)" }}>Confirm Expenses ({expTxns.length})</h2>
                <div className="space-y-2">
                  {expTxns.map((txn, i) => (
                    <ExpenseRow key={txn.id || `txn-${i}`} txn={txn} onConfirm={confirmExpense} onReject={() => setExpTxns(prev => prev.filter((_, idx) => idx !== i))} />
                  ))}
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center py-16">
                <Receipt className="w-10 h-10 mb-4" style={{ color: "#9CA3AF" }} />
                <p style={{ fontSize: "15px", fontWeight: 600, color: "#374151" }}>No pending expense transactions</p>
                <p style={{ fontSize: "13px", color: "#6B7280", marginTop: "8px", textAlign: "center", maxWidth: "360px" }}>Connect your expenses bank or import a CSV to categorise and confirm transactions</p>
                <div className="flex gap-3 mt-5">
                  <label className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium cursor-pointer" style={{ border: "1px solid var(--border)", color: "var(--text-secondary)" }}>
                    <Upload className="w-4 h-4" /> Import CSV
                    <input type="file" accept=".csv" className="hidden" onChange={handleExpCSVImport} />
                  </label>
                  <a href="/settings" className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-semibold" style={{ background: "#E05C2A", color: "#fff" }}>Connect Bank</a>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ExpenseRow({ txn, onConfirm, onReject }) {
  const [category, setCategory] = useState(txn.suggested_category || "other");
  const [merchant, setMerchant] = useState(txn.suggested_merchant || txn.description?.split(" ")[0] || "");

  return (
    <div className="ui-surface p-4 flex items-center gap-4">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-sm font-mono font-bold" style={{ color: "var(--danger)" }}>{formatCurrency(txn.amount)}</span>
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>{txn.date}</span>
        </div>
        <p className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{txn.description}</p>
        <div className="flex items-center gap-2 mt-2">
          <Input value={merchant} onChange={(e) => setMerchant(e.target.value)} placeholder="Merchant" className="h-7 rounded text-xs w-32" style={{ border: "1px solid var(--border)" }} />
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="h-7 rounded text-xs w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="materials">Materials</SelectItem>
              <SelectItem value="tools">Tools</SelectItem>
              <SelectItem value="vehicle">Vehicle</SelectItem>
              <SelectItem value="parking">Parking</SelectItem>
              <SelectItem value="insurance">Insurance</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <Button size="sm" onClick={() => onConfirm(txn, category, merchant)} className="gap-1" style={{ background: "var(--success)", color: "#fff" }}><CheckCircle2 className="w-3.5 h-3.5" /> Confirm</Button>
        <Button size="sm" variant="outline" onClick={onReject}>Skip</Button>
      </div>
    </div>
  );
}
